import React, { useState, useEffect, useRef } from 'react';
import { usePageLoaderContext } from '../../../../contexts/PageLoaderContext';
import { useNotification } from '../../../../contexts/NotificationContext';
import apiService from '../../../../services/api';
import CustomDropdown, { DropdownOption } from '../../../UI/CustomDropdown';
import LeaderboardStats from '../../LeaderBoard/components/LeaderboardStats';
import LeaderboardTabs from '../../LeaderBoard/components/LeaderboardTabs';
import { LeaderboardStatsData } from '../../LeaderBoard/types';
import styles from '../../../../styles/LeaderBoard.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import searchStyles from '../../../../styles/SearchBar.module.css';
import paginationStyles from '../../../../styles/Pagination.module.css';
import GroupLeaderboardTable from './components/GroupLeaderboardTable';

interface GroupDetailsData {
  id: string;
  name: string;
  description: string;
  category: {
    id: string;
    name: string;
    color: string;
  };
  team_lead: {
    id: string;
    full_name: string;
    email: string;
  };
  access_level: string;
  is_active: boolean;
  created_at: string;
  member_count: number;
  theme_color: string;
  logo: string | null;
}

interface GroupLeaderboardEntry {
  rank: number;
  user: {
    id: string;
    full_name: string;
    email: string;
    avatar?: string;
    first_name?: string;
    last_name?: string;
    designation?: string | null;
  };
  points: number;
  tasks_completed: number;
  participation_score: number;
  quality_score?: number;
  efficiency?: number;
  current_streak?: number;
  badges: string[];
  role?: string;
}

interface LeaderboardTabProps {
  groupDetails: GroupDetailsData;
  onGroupUpdate: () => void;
}

const LeaderboardTab: React.FC<LeaderboardTabProps> = ({ groupDetails, onGroupUpdate }) => {
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();

  const [leaderboard, setLeaderboard] = useState<GroupLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [groupStats, setGroupStats] = useState<LeaderboardStatsData>({
    totalPoints: 0,
    totalTasks: 0,
    avgCompletion: 0,
    longestStreak: 0,
    activeMembers: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    activeTab: 'overall',
    timePeriod: 'month',
    category: 'all', // Kept for backward compatibility, but not used in UI
    sortBy: 'points',
    pageSize: 20
  });

  // Signaling refs to prevent duplicate API calls
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const currentGroupIdRef = useRef<string | null>(null);

  const tabOptions = [
    { value: 'overall', label: 'Overall' },
    { value: 'weekly', label: 'Recent Activity' },
    { value: 'monthly', label: 'Current Period' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'quality', label: 'Quality' }
  ];

  const timePeriodOptions: DropdownOption[] = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' }
  ];

  const sortByOptions: DropdownOption[] = [
    { value: 'points', label: 'Total Points' },
    { value: 'tasks', label: 'Tasks Completed' },
    { value: 'quality', label: 'Quality Score' },
    { value: 'efficiency', label: 'Efficiency' },
    { value: 'streak', label: 'Current Streak' }
  ];

  useEffect(() => {
    if (isLoadingRef.current) {
      return;
    }

    const groupHasChanged = currentGroupIdRef.current !== groupDetails.id;

    if (groupHasChanged) {
      currentGroupIdRef.current = groupDetails.id;
      hasLoadedRef.current = false;
    }

    if (!hasLoadedRef.current) {
      loadLeaderboard();
    }
  }, [groupDetails.id, filters.activeTab]);

  // Debounced leaderboard loading - includes search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!hasLoadedRef.current) {
        loadLeaderboard();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.activeTab, filters.timePeriod, filters.sortBy, filters.pageSize, filters.search, currentPage]);

  const loadLeaderboard = async () => {
    if (isLoadingRef.current || isLoadingLeaderboard) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoadingLeaderboard(true);
    setLoading(true);
    showLoader('Loading leaderboard...');

    try {
      // Determine period and sort_by based on active tab
      let period = filters.timePeriod;
      let sortBy = filters.sortBy;
      let tab = filters.activeTab;

      // Tab-based filtering
      if (tab === 'weekly') {
        period = 'week';
        sortBy = sortBy || 'tasks'; // Default to tasks for weekly
      } else if (tab === 'monthly') {
        period = 'month';
        sortBy = sortBy || 'tasks'; // Default to tasks for monthly
      } else if (tab === 'tasks') {
        sortBy = 'tasks';
      } else if (tab === 'quality') {
        sortBy = 'quality';
      } else if (tab === 'overall') {
        // Overall: sort by combined score (tasks + quality)
        sortBy = 'overall'; // Will be handled in backend
      }

      const response = await apiService.getGroupLeaderboard(groupDetails.id, {
        period: period,
        limit: filters.pageSize,
        page: currentPage,
        sort_by: sortBy,
        tab: tab, // Pass tab to backend
        search: filters.search || undefined // Pass search to backend
      }) as any; // Type assertion to access summary property

      if (response.status === 'success') {
        let entries = response.data || [];

        // Search is handled on backend, so entries are already filtered
        setLeaderboard(entries);

        // Update group-wide stats from API summary (entire group performance)
        if (response.summary) {
          setGroupStats({
            totalPoints: response.summary.total_points || 0,
            totalTasks: response.summary.total_tasks || 0,
            avgCompletion: response.summary.avg_participation || 0,
            longestStreak: response.summary.longest_streak || 0,
            activeMembers: response.summary.total_members || 0
          });
        } else {
          // Fallback: calculate from current page data (not ideal, but better than nothing)
          setGroupStats({
            totalPoints: entries.reduce((sum: number, entry: GroupLeaderboardEntry) => sum + entry.points, 0),
            totalTasks: entries.reduce((sum: number, entry: GroupLeaderboardEntry) => sum + entry.tasks_completed, 0),
            avgCompletion: entries.length > 0 ? Math.round(entries.reduce((sum: number, entry: GroupLeaderboardEntry) => sum + entry.participation_score, 0) / entries.length) : 0,
            longestStreak: entries.length > 0 ? Math.max(...entries.map((entry: GroupLeaderboardEntry) => entry.current_streak || 0)) : 0,
            activeMembers: response.pagination?.total_count || entries.length
          });
        }

        // Update pagination state
        if (response.pagination) {
          setTotalPages(response.pagination.total_pages);
          setTotalCount(response.pagination.total_count);
          setCurrentPage(response.pagination.current_page);
        }

        hasLoadedRef.current = true;
        // Highlight first row after loading
        setTimeout(() => {
          setHighlightedRow(0);
        }, 100);
      } else {
        notificationService.error(response.message || 'Failed to load leaderboard');
      }
    } catch (error: any) {
      console.error('Error loading leaderboard:', error);
      notificationService.error(error.response?.data?.message || 'Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingLeaderboard(false);
      hideLoader();
      isLoadingRef.current = false;
    }
  };

  const handleTabChange = (tab: string) => {
    setFilters(prev => {
      // Auto-adjust sort_by based on tab
      let newSortBy = prev.sortBy;
      if (tab === 'tasks') {
        newSortBy = 'tasks';
      } else if (tab === 'quality') {
        newSortBy = 'quality';
      } else if (tab === 'overall') {
        newSortBy = 'overall';
      }

      return { ...prev, activeTab: tab, sortBy: newSortBy };
    });
    setCurrentPage(1);
    setHighlightedRow(null);
    hasLoadedRef.current = false;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setHighlightedRow(null);
    hasLoadedRef.current = false;
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1); // Reset to first page when searching
    hasLoadedRef.current = false;
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    hasLoadedRef.current = false;
  };

  const handlePageSizeChange = (value: string | string[]) => {
    const newPageSize = parseInt(Array.isArray(value) ? value[0] : value);
    setFilters(prev => ({ ...prev, pageSize: newPageSize }));
    setCurrentPage(1);
    hasLoadedRef.current = false;
  };

  // Generate pagination pages
  const generatePaginationPages = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleRowHover = (index: number) => {
    setHighlightedRow(index);
  };

  const handleRowLeave = () => {
    setHighlightedRow(null);
  };

  return (
    <div className={styles.leaderboardContainer}>
      {/* Normal Header */}
      <div className={styles.headerContent} style={{ position: 'relative', padding: '0 1rem 2rem 1rem' }}>
        <div className={styles.headerRow}>
          <div className={styles.searchWrapper}>
            <div className={searchStyles.searchBar}>
              <input
                type="text"
                placeholder="Search by name or email"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <button type="submit">
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.headerActions}>
            {filters.activeTab !== 'weekly' && filters.activeTab !== 'monthly' && (
              <CustomDropdown
                options={timePeriodOptions}
                value={filters.timePeriod}
                onChange={(value) => handleFilterChange('timePeriod', value as string)}
                placeholder="Time Period"
                size="sm"
                variant="outlined"
                className="filter"
              />
            )}
            {filters.activeTab !== 'tasks' && filters.activeTab !== 'quality' && filters.activeTab !== 'overall' && (
              <CustomDropdown
                options={sortByOptions}
                value={filters.sortBy}
                onChange={(value) => handleFilterChange('sortBy', value as string)}
                placeholder="Sort By"
                size="sm"
                variant="outlined"
                className="filter"
              />
            )}
            <CustomDropdown
              options={[
                { value: '10', label: '10 per page' },
                { value: '20', label: '20 per page' },
                { value: '50', label: '50 per page' },
                { value: '100', label: '100 per page' }
              ]}
              value={filters.pageSize.toString()}
              onChange={handlePageSizeChange}
              placeholder="Per Page"
              size="sm"
              variant="outlined"
              className="filter"
            />
            <button
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={() => {
                hasLoadedRef.current = false;
                loadLeaderboard();
              }}
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6"></path>
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                <path d="M3 22v-6h6"></path>
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
              </svg>
              <span className={styles.btnText}>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div style={{ padding: '0' }}>
        {/* Tabs */}
        <LeaderboardTabs
          tabs={tabOptions}
          activeTab={filters.activeTab}
          onTabChange={handleTabChange}
        />

        {/* Stats Grid */}
        {loading ? (
          <div className={styles.statsGrid}>
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className={styles.statCardSkeleton}>
                <div className={styles.skeletonStatIcon}></div>
                <div className={styles.skeletonStatNumber}></div>
                <div className={styles.skeletonStatLabel}></div>
              </div>
            ))}
          </div>
        ) : (
          <LeaderboardStats stats={groupStats} isWorkMode={true} />
        )}

        {/* Leaderboard Table */}
        {loading ? (
          <div className={styles.leaderboardTableContainer}>
            <table className={styles.leaderboardTable}>
              <thead>
                <tr>
                  <th className={styles.rankColumn}>Rank</th>
                  <th className={styles.memberColumn}>Member</th>
                  <th className={styles.pointsColumn}>Points</th>
                  <th className={styles.tasksColumn}>Tasks</th>
                  <th className={styles.qualityColumn}>Quality</th>
                  <th className={styles.actionsColumn}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }, (_, index) => (
                  <tr key={index} className={styles.skeletonRow}>
                    <td className={styles.rankCell}>
                      <div className={styles.skeletonRankBadge}></div>
                    </td>
                    <td className={styles.memberCell}>
                      <div className={styles.skeletonMemberInfo}>
                        <div className={styles.skeletonMemberAvatar}></div>
                        <div className={styles.skeletonMemberDetails}>
                          <div className={styles.skeletonMemberName}></div>
                          <div className={styles.skeletonMemberRole}></div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.pointsCell}>
                      <div className={styles.skeletonStatValue}></div>
                      <div className={styles.skeletonStatLabelSmall}></div>
                    </td>
                    <td className={styles.tasksCell}>
                      <div className={styles.skeletonStatValue}></div>
                      <div className={styles.skeletonStatLabelSmall}></div>
                    </td>
                    <td className={styles.qualityCell}>
                      <div className={styles.skeletonStatValue}></div>
                      <div className={styles.skeletonStatLabelSmall}></div>
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.skeletonActionButton}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <GroupLeaderboardTable
              entries={leaderboard}
              highlightedRow={highlightedRow}
              onRowHover={handleRowHover}
              onRowLeave={handleRowLeave}
            />

            {/* Pagination */}
            {leaderboard.length > 0 && (
              <div className={styles.paginationWrapper}>
                <div className={styles.paginationInfo}>
                  Showing {((currentPage - 1) * filters.pageSize) + 1} to {Math.min(currentPage * filters.pageSize, totalCount)} of {totalCount} members
                </div>
                <div className={paginationStyles.pagination}>
                  {generatePaginationPages().map((page, index) => (
                    <button
                      key={index}
                      className={`${paginationStyles.paginationItem} ${currentPage === page ? paginationStyles.active : ''}`}
                      onClick={() => typeof page === 'number' ? handlePageChange(page) : undefined}
                      disabled={page === '...'}
                      style={page === '...' ? { cursor: 'default', opacity: 0.5 } : {}}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardTab;